import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { StatusChip, BureauChip, TimerBadge, OutcomeChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Download, MailCheck, AlertTriangle, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const MAIL_IMG = "https://images.unsplash.com/photo-1567473030492-533b30c5494c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxlbnZlbG9wZSUyMGNlcnRpZmllZCUyMG1haWx8ZW58MHx8fHwxNzg5MTQzNjQxfDA&ixlib=rb-4.1.0&q=85";

export default function DisputeDetail() {
  const { did } = useParams();
  const [d, setD] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState("deleted");

  const load = () => api.get(`/disputes/${did}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [did]);

  if (!d) return <div className="py-20 text-center text-slate-400 text-sm">Loading dispute…</div>;

  const copyLetter = async () => {
    try {
      await navigator.clipboard.writeText(d.letter_text);
      toast.success("Letter text copied — ready to paste into the bureau portal");
    } catch {
      toast.error("Copy failed — select the text manually");
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/disputes/${did}/send`);
      toast.success(r.data.tracking_number ? `Mailed via certified mail — tracking ${r.data.tracking_number}${r.data.mock ? " (mock mode)" : ""}` : "Submitted to Click2Mail — USPS tracking will appear once the piece is processed");
      setConfirmOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  const refreshTracking = async () => {
    try {
      const r = await api.post(`/disputes/${did}/refresh-tracking`);
      toast[r.data.pending ? "info" : "success"](r.data.pending ? "Click2Mail is still processing — check back shortly" : `USPS tracking: ${r.data.tracking_number}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setStatus = async (status, oc = "") => {
    try {
      await api.patch(`/disputes/${did}/status`, { status, outcome: oc });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="dispute-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/disputes" className="text-xs text-slate-400 hover:text-slate-600">← All disputes</Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">{d.template_name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <StatusChip status={d.status} />
            <BureauChip bureau={d.bureau} />
            <span className="text-xs text-slate-500">Round {d.round} · {d.client_name}</span>
            <TimerBadge daysLeft={d.days_left} status={d.status} />
            <OutcomeChip outcome={d.outcome} />
            {d.mock && <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">MOCK MAIL</span>}
            {d.auto_drafted && <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5" data-testid="auto-drafted-badge">AUTO-DRAFTED · no bureau response</span>}
          </div>
          {d.escalated_from && <p className="text-xs text-slate-500 mt-1">Escalated from <Link to={`/admin/disputes/${d.escalated_from}`} className="text-blue-700 hover:underline" data-testid="escalated-from-link">the previous round</Link> — review the letter, then send.</p>}
          {d.ai_response && (
            <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 max-w-2xl" data-testid="ai-response-summary">
              <div className="font-semibold mb-1">Bureau response (AI-read)</div>
              <p>{d.ai_response.summary}</p>
              <ul className="mt-1.5 space-y-0.5">{(d.ai_response.results || []).map((r) => <li key={r.item_id}><span className="font-semibold uppercase">{r.outcome}</span> — {r.note}</li>)}</ul>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="download-letter-btn"
            onClick={async () => {
              try {
                const r = await api.get(`/disputes/${did}/pdf`, { responseType: "blob" });
                window.open(URL.createObjectURL(r.data), "_blank");
              } catch (e) { toast.error(errMsg(e)); }
            }}>
            <Download className="h-4 w-4 mr-2" /> Letter PDF
          </Button>
          {d.status === "drafted" && (
            <Button onClick={() => setConfirmOpen(true)} data-testid="send-certified-btn" className="bg-[#0B2545] hover:bg-[#134074] text-white">
              <Send className="h-4 w-4 mr-2" /> Send certified mail
            </Button>
          )}
          {["mailed", "in_review"].includes(d.status) && (
            <>
              {d.status === "mailed" && (
                <Button variant="outline" onClick={() => setStatus("in_review")} data-testid="mark-in-review-btn">
                  <MailCheck className="h-4 w-4 mr-2" /> Bureau reviewing
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Select value={outcome} onValueChange={setOutcome}>
                  <SelectTrigger className="w-36" data-testid="outcome-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deleted">Deleted</SelectItem>
                    <SelectItem value="updated">Updated</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => setStatus("resolved", outcome)} data-testid="mark-resolved-btn">Mark resolved</Button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-lg border border-slate-200 bg-white">
          <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">Letter preview</div>
          <pre className="p-5 text-xs leading-relaxed font-mono text-slate-700 whitespace-pre-wrap" data-testid="letter-preview">{d.letter_text}</pre>
        </div>
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm space-y-2">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">Mailing details</div>
            <div><span className="text-slate-400">Mail to:</span><div className="whitespace-pre-line text-slate-700">{d.bureau_info?.address}</div></div>
            <div><span className="text-slate-400">Mailed:</span> {fmtDate(d.mailed_at)}</div>
            <div><span className="text-slate-400">Response due:</span> {fmtDate(d.response_due)}</div>
            <div>
              <span className="text-slate-400">USPS certified tracking:</span>
              <div className="font-mono text-slate-800 mt-0.5" data-testid="certified-mail-barcode-input">{d.tracking_number || (d.c2m_job_id && !d.mock ? "Pending — USPS assigns after processing" : "Assigned on mailing")}</div>
              {d.c2m_job_id && !d.mock && !d.tracking_number && (
                <button onClick={refreshTracking} className="text-xs text-blue-600 hover:underline mt-1" data-testid="refresh-tracking-btn">Check for tracking number</button>
              )}
            </div>
            <div><span className="text-slate-400">Click2Mail job:</span> <span className="font-mono text-xs">{d.c2m_job_id || "—"}</span></div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm space-y-2" data-testid="online-submission-card">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-1">Online submission (optional)</div>
            <p className="text-xs text-slate-500 leading-relaxed">Bureaus only accept online disputes from the consumer — no third-party online filing exists. Send the client the portal link and this letter's text so they can file online free in ~5 minutes, while certified mail stays the official record.</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copyLetter} data-testid="copy-letter-btn"><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy letter text</Button>
              {d.portal?.url && (
                <a href={d.portal.url} target="_blank" rel="noreferrer" data-testid="bureau-portal-link"
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 transition-colors duration-150">
                  <ExternalLink className="h-3.5 w-3.5" /> {d.bureau_info?.name} portal
                </a>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">Disputed items</div>
            <div className="space-y-2">
              {(d.items || []).map((i) => (
                <div key={i.id} className="text-sm flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-slate-800">{i.creditor}</div>
                    <div className="text-xs text-slate-400 capitalize">{i.type.replace("_", " ")} {i.balance ? `· ${money(i.balance)}` : ""}</div>
                  </div>
                  <StatusChip status={i.status} />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">Timeline</div>
            <div className="space-y-3">
              {(d.timeline || []).map((t, idx) => (
                <div key={idx} className="flex gap-2.5">
                  <div className="mt-1.5 h-2 w-2 rounded-full bg-sky-500 shrink-0" />
                  <div>
                    <div className="text-sm text-slate-700">{t.event}</div>
                    <div className="text-xs text-slate-400">{new Date(t.at).toLocaleString()} · {t.by}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Confirm certified mailing</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <img src={MAIL_IMG} alt="Certified mail envelope" className="rounded-lg h-32 w-full object-cover" />
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Mailing is irreversible once submitted. The letter will be sent via Click2Mail <b>Certified Mail with Electronic Return Receipt</b> to {d.bureau_info?.name}, and the 30-day FCRA response clock starts.</span>
            </div>
            <Label className="text-slate-600 text-sm">Destination</Label>
            <div className="text-sm whitespace-pre-line text-slate-800 rounded-md border border-slate-200 p-3">{d.bureau_info?.address}</div>
            <Button onClick={send} disabled={sending} data-testid="confirm-send-mail-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">
              {sending ? "Submitting to Click2Mail…" : "Confirm & mail"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
